import { Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ThemeWrapper } from "@verity/components";
import { CoreButton } from "@verity/components/core";
import { useContext, useState } from "react";
import apiClient from "../../../../helpers/api-client";
import { intIdToUuid } from "../../../../helpers/uuid";
import i18n from "../../../../i18n";
import { PortalControllerContext } from "../../../context";
import InterviewCard from "../InstructorHomePage/Dashboard/components/InterviewCard";
import styles from "./InterviewResultsTable.module.css";
import InterviewResultsEmptyPage from "./pages/InterviewResultsEmptyPage";
import ResultsTableSkeleton from "./ResultsTableSkeleton";
import { InterviewType, NonNullProctoringOptions, SessionType, Status } from "./types";
import { useIsAdmin } from "../../../shared";

function buildSessionRecord(sessions: SessionType[]): Record<string, SessionType> {
	if (!sessions) return {};

	const sessionRecord = sessions.reduce((acc: Record<string, SessionType>, session) => {
		if (!session.assessmentId) return acc;
		acc[session.assessmentId] = session;
		return acc;
	}, {});

	return sessionRecord;
}

const PAGE_SIZE = 10;

export default function InterviewResultsTable() {
	const { portalContext, userInfo } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId, instituteUuid, courseUuid } = portalContext;
	const currentUserUuid = userInfo?.uuid;
	const isAdmin = useIsAdmin();

	const [displayedCount, setDisplayedCount] = useState(PAGE_SIZE);

	const {
		data: interviews,
		error,
		isLoading,
	} = useQuery({
		queryKey: ["institute-interviews-results", lmsProvidedCourseId, instituteUuid, courseUuid, isAdmin ? "admin" : currentUserUuid],
		enabled: !!lmsProvidedCourseId && !!instituteUuid && !!courseUuid && (!!isAdmin || !!currentUserUuid),

		queryFn: async (): Promise<InterviewType[]> => {
			const [interviews, sessionRecord] = await Promise.all([
				apiClient.assessment.getCourseAssessments(lmsProvidedCourseId!),
				apiClient.liveClient
					.getLiveSessionsWithUsers({ instituteUuid: instituteUuid!, courseUuid: courseUuid! })
					.then((sessions) => buildSessionRecord(sessions?.results)),
			]);

			//Add live session and status to interview object
			return interviews
				.map((interview) => ({
					...interview,
					liveSession: sessionRecord[interview.quizId] ?? sessionRecord[intIdToUuid(Number(interview.id))] ?? null,
				}))
				.filter(
					(
						interview,
					): interview is typeof interview & {
						liveSession: SessionType;
						proctoringOptions: NonNullProctoringOptions;
					} =>
						!!interview.liveSession &&
						!!interview.proctoringOptions &&
						!!interview.proctoringOptions.candidateInterviewOptions &&
						new Date() > new Date(interview.liveSession.endTime) &&
						(isAdmin || interview.liveSession.users.some((user) => user.userId === currentUserUuid!))
				)
				.sort((a, b) => new Date(b.liveSession.startTime).getTime() - new Date(a.liveSession.startTime).getTime());
		},
	});

	if (isLoading) return <ResultsTableSkeleton />;

	if (error) {
		console.error("Error fetching interviews:", error);
		throw new Error(error.message);
	}

	return (
		<ThemeWrapper scoped={false}>
			<div className={styles.page}>
				<div className={styles.text}>
					<Typography className={styles.title}>{i18n.t("candidate.resultsPage.title")}</Typography>
					<Typography className={styles.description}>{i18n.t("candidate.resultsPage.description")}</Typography>
				</div>

				{interviews && interviews.length > 0 ? (
					<>
						<Stack gap="10px">
							{interviews?.slice(0, displayedCount)?.map((i) => (
								<InterviewCard key={i.id} interview={{ ...i, status: Status.Completed }} />
							))}
						</Stack>
						{displayedCount < interviews.length && (
							<Box display="flex" justifyContent="center">
								<CoreButton onClick={() => setDisplayedCount((prev) => prev + PAGE_SIZE)}>
									{i18n.t("candidate.resultsPage.button.loadMore")}
								</CoreButton>
							</Box>
						)}
					</>
				) : (
					<InterviewResultsEmptyPage />
				)}
			</div>
		</ThemeWrapper>
	);
}
